import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  bucketLabel,
  bucketOptionsFor,
  RECOGNITION_BUCKET_OPTIONS,
  RECOGNITION_ISSUE_BUCKETS,
  RECOGNITION_OTHER_BUCKET,
} from '../../constants/recognitionReview';

/** Level one: drop the primary bucket entirely. Free at both levels of the taxonomy. */
export const BUCKET_CHOOSER_CLEAR_KEY = 'X';

/** 1 = top-level buckets, 2 = the sub-buckets of one top-level bucket. */
export type BucketChooserLevel = 1 | 2;

export type BucketChooserAction =
  | { kind: 'bucket'; path: string }
  | { kind: 'subBucket'; path: string }
  | { kind: 'clear' };

/**
 * Resolve one keystroke against the bucket taxonomy for the chooser level it was typed at.
 *
 * Shortcut letters are unique within a level but repeat across levels, so the level - and, at level
 * two, the top-level bucket already chosen - decides what a letter means.
 *
 * @param level Chooser level the key was pressed at.
 * @param key The pressed key; case is ignored.
 * @param prefix The chosen top-level bucket path; required at level two, ignored at level one.
 * @returns The action the key selects, or `null` when the level has no option for it.
 */
export function bucketActionFromShortcutKey(
  level: BucketChooserLevel,
  key: string,
  prefix?: string | null
): BucketChooserAction | null {
  const normalizedKey = key.toUpperCase();
  if (level === 1) {
    if (normalizedKey === BUCKET_CHOOSER_CLEAR_KEY) return { kind: 'clear' };
    const option = RECOGNITION_BUCKET_OPTIONS.find(
      (candidate) => candidate.shortcutKey === normalizedKey
    );
    return option ? { kind: 'bucket', path: option.path } : null;
  }
  const option = bucketOptionsFor(prefix ?? '').find(
    (candidate) => candidate.shortcutKey === normalizedKey
  );
  return option ? { kind: 'subBucket', path: option.path } : null;
}

/**
 * Full-page keyboard chooser for the Recognition issue bucket taxonomy.
 *
 * Every category is shown with all of its leaf sub-buckets expanded, so a mouse reviewer clicks the
 * leaf directly. The keyboard flow keeps the historical two-keystroke shape: a category letter arms
 * that category (the rest of the grid dims), then the sub letter picks the leaf. Escape backs out
 * of an armed category before it closes the chooser. Every other keystroke is swallowed while the
 * chooser is open so page shortcuts cannot fire.
 */
export const BucketChooserOverlay: React.FC<{
  selectedPath?: string | null;
  onSelect: (path: string | null) => void;
  onClose: () => void;
}> = ({ selectedPath = null, onSelect, onClose }) => {
  const [prefix, setPrefix] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const level: BucketChooserLevel = prefix ? 2 : 1;

  // The chooser declares `aria-modal`, so focus has to actually enter it or screen readers keep
  // announcing the trigger behind it and Tab walks the page underneath. Focus goes back to whatever
  // opened the chooser on close, which is how the keyboard reviewer returns to the verdict block.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  const applyAction = useCallback(
    (action: BucketChooserAction) => {
      if (action.kind === 'clear') {
        onSelect(null);
        return;
      }
      if (action.kind === 'bucket') {
        setPrefix(action.path);
        return;
      }
      onSelect(action.path);
    },
    [onSelect]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      ) {
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (prefix) setPrefix(null);
        else onClose();
        return;
      }
      if (event.key === 'Tab') {
        const focusable = Array.from(
          dialogRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          ) ?? []
        );
        if (focusable.length === 0) {
          event.preventDefault();
          dialogRef.current?.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        // Shift+Tab must also wrap from the container itself: the dialog carries focus on open
        // (tabIndex -1), and from there the browser would otherwise step OUT of the trap to the
        // page behind it. Same guard as the RecognitionInspectorModal.
        if (
          event.shiftKey &&
          (document.activeElement === first || document.activeElement === dialogRef.current)
        ) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (event.key.length !== 1 || event.metaKey || event.ctrlKey || event.altKey) return;
      // Swallow every printable key while the chooser owns the keyboard, matched or not.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const action = bucketActionFromShortcutKey(level, event.key, prefix);
      if (action) applyAction(action);
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [applyAction, level, onClose, prefix]);

  return (
    <div className="rr-bucket-backdrop" onClick={onClose}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="rr-bucket-chooser rr-bucket-chooser--full"
        role="dialog"
        aria-modal="true"
        aria-label="Recognition issue bucket"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="rr-bucket-header">
          <div>
            <div className="rr-bucket-title">Recognition issue bucket</div>
            <div className="rr-bucket-copy">
              What single change would most likely have made this sample confident correct? Click a
              cause, or type the category letter and then the cause letter.
            </div>
          </div>
          <button
            type="button"
            className="rr-bucket-close"
            aria-label="Close bucket chooser"
            onClick={onClose}
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </button>
        </div>
        <div className="rr-bucket-grid">
          {RECOGNITION_ISSUE_BUCKETS.map((definition) => {
            const isArmed = prefix === definition.path;
            return (
              <section
                key={definition.path}
                data-testid={`bucket-chooser-group-${definition.path}`}
                aria-label={definition.label}
                className={`rr-bucket-group${isArmed ? ' is-armed' : ''}${
                  prefix && !isArmed ? ' is-dimmed' : ''
                }`}
              >
                <div className="rr-bucket-group-head">
                  <span className="rr-bucket-group-title">{definition.label}</span>
                  <span className="rr-bucket-key">{definition.shortcutKey}</span>
                </div>
                <div className="rr-bucket-owner">
                  {definition.owner}
                  {definition.bucket === RECOGNITION_OTHER_BUCKET ? ' · requires a note' : ''}
                </div>
                <div className="rr-bucket-group-options">
                  {definition.subBuckets.map((option) => (
                    <button
                      key={option.path}
                      type="button"
                      data-testid={`bucket-chooser-option-${option.path}`}
                      className={`rr-bucket-option${
                        selectedPath === option.path ? ' is-selected' : ''
                      }`}
                      onClick={() => applyAction({ kind: 'subBucket', path: option.path })}
                    >
                      <span>{option.label}</span>
                      <span className="rr-bucket-key">{option.shortcutKey}</span>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
        <div className="rr-bucket-footer">
          <div className="rr-bucket-hint">
            {prefix
              ? `${bucketLabel(prefix)} is armed: press a cause letter, or Esc to disarm it.`
              : 'Esc closes this chooser.'}
          </div>
          <button
            type="button"
            data-testid="bucket-chooser-clear"
            className="rr-bucket-option is-clear"
            onClick={() => applyAction({ kind: 'clear' })}
          >
            <span>Clear primary bucket</span>
            <span className="rr-bucket-key">{BUCKET_CHOOSER_CLEAR_KEY}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BucketChooserOverlay;
